import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (localStorage.getItem("es_admin_token")) navigate("/admin/orders", { replace: true });
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem("es_admin_token", data.access_token);
      localStorage.setItem("es_admin_user", JSON.stringify(data.user));
      navigate("/admin/orders", { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="flex min-h-screen items-center justify-center bg-[#0a0a0c] px-6 py-24"
    >
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.19, 1, 0.22, 1] }}
        className="w-full max-w-md space-y-8"
      >
        <div className="text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-[#c6a87c]/40 text-[#c6a87c]">
            <Lock size={18} strokeWidth={1.4} />
          </div>
          <p className="mt-6 font-tech text-[10px] uppercase tracking-[0.4em] text-[#c6a87c]">
            Admin Portal
          </p>
          <h1 className="mt-4 font-serif-display text-3xl text-white lg:text-4xl">
            Eurosteel Orders
          </h1>
          <p className="mt-3 font-body text-sm text-white/50">Sign in to manage order status.</p>
        </div>

        <div className="space-y-5">
          <div>
            <label className="mb-2 block font-tech text-[10px] uppercase tracking-[0.3em] text-white/50">
              Email
            </label>
            <input
              data-testid="admin-login-email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full border-b border-white/15 bg-transparent py-3 font-body text-base text-white placeholder:text-white/30 focus:border-[#c6a87c] focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-2 block font-tech text-[10px] uppercase tracking-[0.3em] text-white/50">
              Password
            </label>
            <input
              data-testid="admin-login-password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full border-b border-white/15 bg-transparent py-3 font-body text-base text-white placeholder:text-white/30 focus:border-[#c6a87c] focus:outline-none"
            />
          </div>
        </div>

        {error && (
          <p
            data-testid="admin-login-error"
            className="border-l-2 border-red-400/70 bg-red-500/5 p-4 font-body text-sm text-red-100/90"
          >
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="flex w-full items-center justify-center gap-2 rounded-full bg-[#c6a87c] py-4 font-tech text-[11px] uppercase tracking-[0.24em] text-[#0a0a0c] transition-all duration-500 hover:bg-[#d8bd93] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Signing in
            </>
          ) : (
            "Sign in"
          )}
        </button>

        <p className="text-center font-tech text-[10px] uppercase tracking-[0.3em] text-white/25">
          Restricted access
        </p>
      </motion.form>
    </div>
  );
}
